import { useRef, useCallback } from "react"

type NativeInputElement = HTMLInputElement | HTMLTextAreaElement

const claimedNativeInputEvents = new WeakSet<Event>()

/**
 * Claims the exact native event for one dispatch path. React 19 may invoke its
 * delegated handler before or after the target's native listener, so event
 * identity keeps suppression order-independent without merging distinct events.
 */
export function claimNativeInputSyncDispatch(event: Event): boolean {
  if (claimedNativeInputEvents.has(event)) return false
  claimedNativeInputEvents.add(event)
  return true
}

/**
 * Attaches a native DOM "input" event listener to the given ref element.
 * When Playwright dispatches synthetic input events (page.fill / page.type),
 * React's synthetic onChange may not fire. This hook catches the native event
 * and calls onNativeInput with the current value.
 *
 * Safe for user typing: the native listener and React's delegated onChange
 * claim the same Event object. Whichever path observes it first dispatches;
 * browsers where React misses the event still use the native path.
 */
export function useNativeInputSync(
  onNativeInput: (value: string) => void,
): React.RefCallback<HTMLInputElement | HTMLTextAreaElement> {
  const callbackRef = useRef(onNativeInput)
  const attachedNodeRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
  const nativeHandlerRef = useRef<((event: Event) => void) | null>(null)
  callbackRef.current = onNativeInput

  const refCallback = useCallback((node: HTMLInputElement | HTMLTextAreaElement | null) => {
    const attachedNode = attachedNodeRef.current
    const attachedHandler = nativeHandlerRef.current
    if (attachedNode && attachedHandler) {
      attachedNode.removeEventListener("input", attachedHandler)
      if ((attachedNode as any).__cogoCleanup) {
        delete (attachedNode as any).__cogoCleanup
      }
    }
    attachedNodeRef.current = null
    nativeHandlerRef.current = null
    if (!node) return
    const handler = (e: Event) => {
      if (!claimNativeInputSyncDispatch(e)) return
      const target = e.target as NativeInputElement
      callbackRef.current(target.value)
    }
    node.addEventListener("input", handler)
    attachedNodeRef.current = node
    nativeHandlerRef.current = handler
    ;(node as any).__cogoCleanup = () => {
      node.removeEventListener("input", handler)
      if (attachedNodeRef.current === node) {
        attachedNodeRef.current = null
        nativeHandlerRef.current = null
      }
    }
  }, [])

  return refCallback
}
